import { QueryKey } from "@tanstack/react-query";
import { ResponseError } from "@wingspanhq/wingspan-api-sdk";
import {
  useWSMutation,
  WSMutationConfig,
  WSMutationResult
} from "@ws-react-query";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import {
  resolveDefaultDependencies,
  V3DefaultDependencies,
  V3DefaultDependenciesLevel
} from "./createV3MutationDependencies";
import { SdkEndpointMethod, V3QueryMethodName } from "./createV3QueryHook";

/**
 * The mutation-hook config: the platform {@link WSMutationConfig} (typed off the
 * SDK method — `TResult` is the response model, `TVariables` the request model)
 * plus the v3-only `defaultDependenciesLevel`.
 *
 * On success the hook auto-invalidates sibling read queries derived from the
 * method name (see {@link createV3Queries}). `defaultDependenciesLevel` chooses
 * how wide — `"entity"` (the default), `"api"`, or `"none"`.
 *
 * The defaults compose with — never replace — your own keys:
 * - `dependencies` you pass are merged with the default keys (deduped), and the
 *   whole set is background-invalidated.
 * - `awaitDependencies` is left exactly as you pass it and is refetched-and-awaited
 *   alongside the background defaults, so you can await a specific read while the
 *   rest refresh in the background.
 *
 * Build your keys from the query registry — `entry.getQueryKey(request)` for one
 * cached request, `entry.queryKeyBase` for every variant of a method — to stay
 * fully typed and string-free.
 *
 * @example
 * // entity defaults (getAccount + listAccounts + searchAccounts) refresh in the
 * // background; the person read is additionally awaited before the mutation settles
 * mutations.accountsApi.updateAccount.useMutation({
 *   awaitDependencies: (_res, req) => [
 *     queries.personsApi.getPerson.getQueryKey({ personId: req.accountUpdate.ownerId })
 *   ],
 *   dependencies: (_res, req) => [
 *     queries.invoicesApi.searchInvoices.queryKeyBase
 *   ]
 * });
 */
export type V3MutationConfig<TRequest, TResponse> = WSMutationConfig<
  TResponse,
  ResponseError,
  TRequest
> & {
  defaultDependenciesLevel?: V3DefaultDependenciesLevel;
};

/**
 * The mutation hook produced by {@link createV3MutationHook}: mirrors the SDK
 * call 1:1 (request model in, response model out) and returns the platform
 * `[mutate, meta]` tuple. The request is passed at call time — `mutate(request)`.
 */
export type V3MutationHook<TRequest, TResponse> = (
  config?: V3MutationConfig<TRequest, TResponse>
) => WSMutationResult<TResponse, ResponseError, TRequest>;

/** Resolves the typed mutation hook for a given SDK method type. */
type V3MutationHookFor<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<infer TResponse>
  ? V3MutationHook<TRequest, TResponse>
  : never;

const EMPTY_DEFAULTS: V3DefaultDependencies = { api: [], entity: [] };

export const resolveDependencies = (
  dependencies: WSMutationConfig<unknown>["dependencies"],
  result: unknown,
  variables: unknown
): QueryKey[] =>
  (typeof dependencies === "function"
    ? dependencies(result, variables)
    : (dependencies ?? [])) as QueryKey[];

/**
 * Fold the method's default dependency keys into the call config: strip the
 * v3-only `defaultDependenciesLevel`, then prepend the level's keys to whatever
 * `dependencies` the caller passed (deduped, so an overlapping explicit key
 * never triggers a second invalidation). Returns the config untouched when there
 * is nothing to inject, preserving a bare `undefined` passthrough.
 */
const applyDefaultDependencies = (
  config: V3MutationConfig<unknown, unknown> | undefined,
  defaults: V3DefaultDependencies
): WSMutationConfig<unknown, ResponseError, unknown> | undefined => {
  const level = config?.defaultDependenciesLevel ?? "entity";
  const defaultKeys = resolveDefaultDependencies(level, defaults);

  if (
    defaultKeys.length === 0 &&
    config?.defaultDependenciesLevel === undefined
  ) {
    return config;
  }

  const { defaultDependenciesLevel, dependencies, ...rest } = config ?? {};

  if (defaultKeys.length === 0) {
    return dependencies === undefined ? rest : { ...rest, dependencies };
  }

  return {
    ...rest,
    dependencies: (result, variables) =>
      uniqWith(
        [
          ...defaultKeys,
          ...resolveDependencies(dependencies, result, variables)
        ],
        isEqual
      )
  };
};

/**
 * Build a fully-typed React Query mutation hook from an SDK API instance + method
 * name — the write-side sibling of {@link createV3QueryHook}.
 *
 * The generated hook mirrors the SDK call 1:1 (same request type, same response
 * type) and forwards a {@link V3MutationConfig} to `useWSMutation`, so the
 * platform's `dependencies` / `awaitDependencies` invalidation works unchanged.
 * On top of that it auto-injects `defaultDependencies` (precomputed by
 * {@link createV3Queries}) selected by the config's `defaultDependenciesLevel`.
 * There is no cache key to derive — mutations don't read the cache — so unlike
 * the query hook it takes no `queryKey` argument.
 *
 * @param api        An instantiated SDK API client, e.g. `accountsApi`.
 * @param methodName A write method on `api` (`createAccount`, `updateAccount`,
 *                   `deleteAccount`, `moveAccount`, …). Only clean endpoint
 *                   methods are offered — `*Raw` / `*RequestOpts` are excluded.
 * @param defaultDependencies The per-level default invalidation keys for this
 *                   method. Omitted for a standalone hook → no auto-invalidation.
 *
 * @example
 * // src/v3/query/platform/account/mutations/useMutationUpdateAccount.ts
 * import { accountsApi } from "@api";
 * export const useMutationUpdateAccount = createV3MutationHook(
 *   accountsApi,
 *   "updateAccount"
 * );
 * const [updateAccount, updateAccountMeta] = useMutationUpdateAccount();
 * await updateAccount({ accountId, accountUpdate });
 */
export function createV3MutationHook<
  TApi extends object,
  TName extends V3QueryMethodName<TApi>
>(
  api: TApi,
  methodName: TName,
  defaultDependencies: V3DefaultDependencies = EMPTY_DEFAULTS
): V3MutationHookFor<TApi[TName]> {
  const callEndpoint = (api[methodName] as unknown as SdkEndpointMethod).bind(
    api
  );

  const useGeneratedMutation = (
    config?: V3MutationConfig<unknown, unknown>
  ): WSMutationResult<unknown, ResponseError, unknown> =>
    useWSMutation<unknown, ResponseError, unknown>(
      (request: unknown) => callEndpoint(request),
      applyDefaultDependencies(config, defaultDependencies)
    );

  return useGeneratedMutation as unknown as V3MutationHookFor<TApi[TName]>;
}
